import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { CheckCircleIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import type { AwardCriterionDto } from "../../../../../autogen/bff-api";

interface Props {
  criteria: AwardCriterionDto[];
}

// t('Sum of criterion weights (of the total)') t('must be 100%')

export const WeightSummary = ({ criteria }: Props) => {
  const { t } = useTranslation();
  // Nothing to sum yet, so nothing to say: before the first criterion exists "0 % — må være 100 %" is a
  // complaint about work the buyer has not started, phrased as though they had got it wrong. The rule
  // lives here rather than at each call site because it is a property of the sum itself, and the two
  // callers would otherwise have to remember it separately.
  if (criteria.length === 0) return null;

  const total = criteria.reduce((sum, c) => sum + (c.weight?.number ?? 0), 0);
  const isValid = total === 100;

  return (
    <Box
      data-testid="weight-summary"
      data-valid={isValid}
      // Full width rather than hugging its text: it is the table's bottom line, and a short pill floating
      // under a wide table reads as a stray note instead of the sum of what is above it.
      display="flex"
      borderWidth="1px"
      borderRadius="lg"
      borderColor={isValid ? "smSuccessSoftBorder" : "smWarningSoftBorder"}
      bg={isValid ? "smSuccessSoft" : "smWarningSoft"}
      px="4"
      py="2"
      mt="4"
      mb="6"
    >
      <Flex align="center" gap="2" wrap="wrap">
        <Icon
          as={isValid ? CheckCircleIcon : WarningTwoIcon}
          color={isValid ? "smSuccess" : "smWarning"}
          boxSize="3.5"
        />
        <Text fontSize="sm">{t("Sum of criterion weights (of the total)")}:</Text>
        <Text fontWeight="bold" fontSize="sm" color={isValid ? "smSuccess" : "smWarning"}>
          {total}%
        </Text>
        {!isValid && (
          <Text fontSize="sm" color="smWarning">
            — {t("must be 100%")}
          </Text>
        )}
      </Flex>
    </Box>
  );
};
